/**
 * packages/auth-client/src/useAuth.js
 * React hook for auth state in core-loop.dev browser apps.
 * Requires React 18+ as a peer dependency.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  decodeJwtPayload,
  isTokenExpired,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  clearStoredRefreshToken,
} from './utils.js';

interface UseAuthOptions {
  /**
   * Auth API base URL.
   * When provided, the hook will automatically refresh the access token
   * ~2 minutes before it expires using the stored refresh token cookie.
   */
  refreshUrl?: string;
}

const DEFAULT_REFRESH_URL = 'https://auth.core-loop.dev';

export function useAuth(opts?: UseAuthOptions) {
  const refreshUrl = opts?.refreshUrl ?? DEFAULT_REFRESH_URL;
  // Determine initial token synchronously to avoid a render-cycle flash.
  const initialToken = (() => {
    const stored = getStoredToken();
    if (stored && !isTokenExpired(stored)) return stored;
    clearStoredToken();
    return null;
  })();

  const [token, setToken] = useState<string | null>(initialToken);

  // If the access token is missing or expired, attempt a silent refresh before
  // showing any UI. The HttpOnly refresh cookie is visible only to the server.
  // isLoading stays true until that attempt completes.
  const needsSilentRefresh = !initialToken;
  const [isLoading, setIsLoading] = useState(needsSilentRefresh);

  useEffect(() => {
    if (!needsSilentRefresh) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${refreshUrl}/api/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json() as { accessToken: string };
            setStoredToken(data.accessToken);
            setToken(data.accessToken);
          } else {
            clearStoredToken();
            clearStoredRefreshToken();
          }
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [needsSilentRefresh, refreshUrl]);

  // Auto-refresh: schedule a refresh ~2 min before access token expiry
  useEffect(() => {
    if (!token) return;

    const payload = decodeJwtPayload(token);
    const exp = typeof payload?.exp === 'number' ? payload.exp : null;
    if (!exp) return;

    const expiresInMs = exp * 1000 - Date.now();
    const refreshInMs = Math.max(expiresInMs - 2 * 60 * 1000, 0);

    const refreshAccessToken = async () => {
      try {
        const res = await fetch(`${refreshUrl}/api/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json() as { accessToken: string };
          setStoredToken(data.accessToken);
          setToken(data.accessToken);
        } else {
          // Refresh rejected -- force re-login
          clearStoredToken();
          clearStoredRefreshToken();
          setToken(null);
        }
      } catch {
        // Network error -- don't clear; will retry on next token change
      }
    };

    if (refreshInMs <= 0) {
      void refreshAccessToken();
      return;
    }

    const timer = setTimeout(() => { void refreshAccessToken(); }, refreshInMs);

    return () => clearTimeout(timer);
  }, [token, refreshUrl]);

  const login = useCallback((newToken: string) => {
    setStoredToken(newToken);
    setToken(newToken);
  }, []);

  const logout = useCallback((redirectUrl?: string) => {
    clearStoredToken();
    clearStoredRefreshToken();
    setToken(null);
    void fetch(`${refreshUrl}/api/auth/signout`, { method: 'POST', credentials: 'include' });
    if (typeof window !== 'undefined') {
      // Default to the current app's origin so each app stays on its own domain
      // after sign-out. Pass a string redirectUrl to override (e.g. to a specific path).
      // Guard: ignore non-string arguments (e.g. click events passed by onClick handlers).
      const dest = typeof redirectUrl === 'string' ? redirectUrl : window.location.origin;
      window.location.href = dest;
    }
  }, [refreshUrl]);

  const user = token && !isTokenExpired(token) ? decodeJwtPayload(token) : null;

  return { user, token, isLoading, login, logout };
}
